import type { ElementType } from "react";
import {
    Bolt,
    DirectionsCar,
    Groups,
    LocalFireDepartment,
    Pets,
    Settings,
    WarningAmber,
    Waves,
} from "@mui/icons-material";

/**
 * Client-side hazard taxonomy for the hazard picker's category sidebar.
 *
 * The backend `Hazard` model has no category field, so this map groups the
 * library hazards by name for presentation only. `categorizeHazard` falls back
 * to "general" for any hazard not listed here (e.g. new library hazards or
 * custom ones), so the picker never drops a hazard. Category counts are derived
 * at runtime from the actual API hazard set, not hard-coded.
 */
export type HazardCategoryKey =
    | "physical"
    | "electrical"
    | "fire"
    | "workplace"
    | "vehicles"
    | "water"
    | "animals"
    | "general";

export interface HazardCategoryMeta {
    key: HazardCategoryKey;
    label: string;
    Icon: ElementType;
}

export const GENERAL_CATEGORY: HazardCategoryKey = "general";

export const hazardCategories: HazardCategoryMeta[] = [
    { key: "physical", label: "Physical & Mechanical", Icon: Settings },
    { key: "electrical", label: "Electrical & Energy", Icon: Bolt },
    { key: "fire", label: "Fire, Heat & Chemical", Icon: LocalFireDepartment },
    { key: "workplace", label: "Workplace & People", Icon: Groups },
    { key: "vehicles", label: "Vehicles & Transport", Icon: DirectionsCar },
    { key: "water", label: "Water & Coastal", Icon: Waves },
    { key: "animals", label: "Animals & Nature", Icon: Pets },
    { key: "general", label: "General", Icon: WarningAmber },
];

const categoryHazardNames: Record<HazardCategoryKey, string[]> = {
    physical: [
        "Automatic start-up",
        "Barbed wire",
        "Compressors",
        "Confined spaces",
        "Counter-rotating rollers",
        "Crushing by moving parts",
        "Crushing of hands",
        "Drop or fall hazard",
        "Falling Parts",
        "Floor-level obstacle",
        "Fragile roof",
        "Hand injury from rapid movement of workpiece in press brake machine",
        "Hand injury in press",
        "Machinery",
        "No guard rail and unprotected edges",
        "Overhead obstacle",
        "Overhead or suspended load",
        "Rapid movement of workpiece in press brake machine",
        "Sharp element",
        "Slipper surface",
        "Use of Machinery",
        "Working at height",
    ],
    electrical: [
        "Arc flash hazard",
        "Battery charging",
        "Electrical Safety",
        "Electricity hazard",
        "Laser beam",
        "Magnetic field",
        "Non-ionizing radiation",
        "Optical radiation",
        "Pressurized cylinder",
        "Radioactive material or ionizing radiation",
    ],
    fire: [
        "Asbestos",
        "Biological hazard",
        "Breathing hazard",
        "Chemical burns to skin and delicate areas",
        "Corrosive substance",
        "Dust",
        "Explosive materials",
        "Fire Safety",
        "Flammable material",
        "Gas Safety",
        "Hot surface",
        "Ingestion of hazardous substance",
        "Low temperature hazard",
        "Oxidizing substance",
        "Toxic material",
    ],
    workplace: [
        "Display screen equipment",
        "Lone working",
        "Manual Handling",
        "New and Expectant Mothers",
        "Noise",
        "Stress",
        "Sudden loud noises",
        "Vibration",
        "Violence & Aggression",
    ],
    vehicles: [
        "Forklift truck and other industrial vehicles",
        "Injury from automated or remote-controlled vehicles",
        "Wheeled beach vehicles; land surfing; sand yachting; wind carts",
    ],
    water: [
        "Boating area",
        "Deep water",
        "Falling Ice Spikes",
        "High surf or large breaking waves",
        "Incoming tides",
        "Kite surfing",
        "Parasailing",
        "Quicksand, mud, deep mud, or silt",
        "Roof avalanche",
        "Sewage effluent outfall",
        "Shallow water",
        "Slipway",
        "Strong currents",
        "Strong winds",
        "Submerged objects",
        "Sudden drop in swimming pool",
        "Sudden drop to deep water",
        "Surfing",
        "Thin ice",
        "Tsunami hazard zone",
        "Unstable cliff",
        "Unstable cliff edge",
        "Water skiing or towed water activity area",
    ],
    animals: ["Alligators, caymans and crocodiles", "Bull", "Guard dog", "Sharks"],
    general: ["External and Internal events", "General warning"],
};

const hazardNameToCategory = new Map<string, HazardCategoryKey>();
(Object.keys(categoryHazardNames) as HazardCategoryKey[]).forEach((key) => {
    categoryHazardNames[key].forEach((name) => {
        hazardNameToCategory.set(name.trim().toLowerCase(), key);
    });
});

export function categorizeHazard(name?: string): HazardCategoryKey {
    return hazardNameToCategory.get((name ?? "").trim().toLowerCase()) ?? GENERAL_CATEGORY;
}
